"use client"

import { useCallback, useEffect, useId, useRef, useState } from "react"
import Link from "next/link"
import { createClient } from "@/lib/supabase/client"
import { isNativeApp } from "@/lib/native"

/**
 * PublicNavGlass — the public marketing navbar.
 *
 * A single glass shell that lives at the top of the viewport and morphs
 * between two states instead of mounting a separate overlay:
 *
 *   collapsed → a compact pill: [≡ Menu] ......... [◐] [12:10]
 *   expanded  → the same pill sitting inside a frosted panel that grows
 *               underneath it (Menu / Company / Legal / Social).
 *
 * The growth is a `grid-template-rows: 0fr → 1fr` transition, so the panel
 * animates to its natural height without any measuring, and rows fade in on
 * a stagger behind it. Everything is driven by CSS custom properties set on
 * the shell (see TOKENS) so the same component reads correctly on the black
 * surface and on the light/cream one.
 *
 * Colour mode is controllable: pass `mode`/`onModeChange` to lift the state
 * (the landing does this so the page repaints with the nav), or leave both
 * off and the sun/moon button manages it internally.
 *
 * It started as the landing's nav and is now the chrome for every marketing
 * page — traction, press, changelog, pricing, the legal set. Two things came
 * with that move:
 *
 *   `defaultMode` lets a page that can't repaint itself still hand the nav the
 *   right palette (dark on /traction, light on /press), and `showModeToggle`
 *   lets it hide the sun/moon rather than offer a switch that would only
 *   recolour the menu and leave the page behind.
 *
 *   Auth is resolved here rather than passed in, so a server-rendered page can
 *   drop the nav in with no props. Signed in, the Join pill becomes Dashboard
 *   and the panel's Sign Up / Log In pair becomes Dashboard / Sign Out — the
 *   same nav, addressed to someone who already has an account.
 */

export type GlassNavMode = "dark" | "light"

/* Who the landing is currently talking to. Lives here rather than in the
   landing so the nav can take it as a prop without a circular import. */
export type NavAudience = "barber" | "owner" | "client"

/* `only` narrows a link to specific audiences; omit it and the link shows for
   everyone. The landing's switch drives this, so a client is never offered the
   job board and an owner is never sent to browse the shops they already run. */
/* `webOnly` marks a link that exists to sell BOOTH rather than to run it —
   the journal, the press page, the changelog, the pricing table. Those are
   the website's job. Inside the iOS/Android shell they are filtered out, so
   nobody taps a nav item in the app and lands on a marketing page they
   can't get back from. The product surfaces (/listings, /search, the
   signed-in actions) stay, because browsing shops IS the product. */
type NavLink = { label: string; href: string; external?: boolean; only?: NavAudience[]; webOnly?: boolean }

const forAudience = (links: NavLink[], audience?: NavAudience, native?: boolean) =>
  links.filter((l) => (!l.only || !audience || l.only.includes(audience)) && !(native && l.webOnly))

// Primary destinations — the large type in the panel, mirroring the public
// surfaces a logged-out visitor can actually reach.
const PRIMARY: NavLink[] = [
  { label: "Browse Shops", href: "/listings", only: ["barber", "client"] },
  // The chat-style "who's hiring" search — a faster path into the same
  // open-booth data than filtering /listings by hand.
  { label: "Search", href: "/search", only: ["barber", "client"] },
  // The three signed-in actions go to sign in (2026-09-20), each carrying
  // the page it continues to. Join, next to them in the bar, is the signup.
  { label: "Pay booth rent", href: "/auth/login?redirect=/dashboard/barber", only: ["barber", "client"] },
  { label: "List your shop", href: "/auth/login?redirect=/dashboard/owner/create-shop", only: ["owner"] },
  { label: "Post an opening", href: "/auth/login?redirect=/hiring", only: ["owner"] },
  // Pricing is what a shop pays BOOTH — barbers pay rent to shops, clients
  // pay nothing, so neither has a reason to see it.
  { label: "Pricing", href: "/pricing", only: ["owner"], webOnly: true },
  // "/#features" is an anchor on the marketing landing page, which the app
  // deliberately never shows.
  { label: "Features", href: "/#features", webOnly: true },
]

// Secondary — brand/company surfaces, two columns of quiet links.
const COMPANY: NavLink[] = [
  { label: "Articles", href: "/blog", webOnly: true },
  { label: "Press", href: "/press", webOnly: true },
  { label: "Traction", href: "/traction", only: ["barber", "owner"], webOnly: true },
  { label: "Building in Public", href: "/changelog", only: ["barber", "owner"], webOnly: true },
  // Partner perks — supplies, wellness, travel. A trade program, not a
  // consumer one.
  // The public /rewards page is the pitch for the programme. Signed-in
  // barbers reach the real thing at /dashboard/barber/rewards from the
  // app's own nav, so this one is the website's copy.
  { label: "Rewards", href: "/rewards", only: ["barber", "owner"], webOnly: true },
  // The demo is a shop sales motion.
  { label: "Book a Demo", href: "https://cal.com/boothdemo/30min", external: true, only: ["owner"], webOnly: true },
]

// Every colour in the component resolves from these. Keeping them in one
// place is what lets the panel, the pill and the page stay in step when the
// mode flips mid-animation.
const TOKENS: Record<GlassNavMode, Record<string, string>> = {
  dark: {
    "--nav-fg": "#F7F7F5",
    "--nav-muted": "rgba(247,247,245,0.44)",
    "--nav-line": "rgba(255,255,255,0.09)",
    "--nav-panel": "rgba(8,8,9,0.72)",
    "--nav-panel-edge": "rgba(255,255,255,0.11)",
    // Black base with a glossy top sheen baked into the same value (two
    // gradient layers over a near-black fill) rather than a flat white
    // wash — the old 7% white tint read as plain gray once blurred over
    // the black page instead of glass sitting on black.
    "--nav-pill":
      "linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.03) 30%, transparent 60%), rgba(6,6,7,0.7)",
    "--nav-pill-edge": "rgba(255,255,255,0.13)",
    "--nav-pill-fg": "#FFFFFF",
    "--nav-chip": "rgba(255,255,255,0.05)",
    "--nav-chip-edge": "rgba(255,255,255,0.10)",
    "--nav-gloss": "rgba(255,255,255,0.14)",
    "--nav-shadow": "0 30px 70px -28px rgba(0,0,0,0.9)",
    "--nav-accent": "rgba(247,247,245,0.62)",
  },
  light: {
    "--nav-fg": "#14120F",
    "--nav-muted": "rgba(20,18,15,0.48)",
    "--nav-line": "rgba(20,18,15,0.10)",
    "--nav-panel": "rgba(243,241,235,0.78)",
    "--nav-panel-edge": "rgba(255,255,255,0.7)",
    // Same top sheen as the dark pill over a near-solid black, so the bar
    // on a light page (/listings, /pricing) reads as the landing's glass
    // rather than a flat dark grey.
    "--nav-pill":
      "linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.03) 30%, transparent 60%), rgba(6,6,7,0.94)",
    "--nav-pill-edge": "rgba(255,255,255,0.14)",
    "--nav-pill-fg": "#FBFBF9",
    "--nav-chip": "rgba(255,255,255,0.6)",
    "--nav-chip-edge": "rgba(20,18,15,0.08)",
    "--nav-gloss": "rgba(255,255,255,0.55)",
    "--nav-shadow": "0 30px 70px -30px rgba(35,30,24,0.45)",
    "--nav-accent": "rgba(20,18,15,0.62)",
  },
}

// Fine noise laid over the glass so the blur reads matte instead of plastic.
export const GRAIN_URL =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)"

export function PublicNavGlass({
  mode: modeProp,
  onModeChange,
  defaultMode = "dark",
  showModeToggle = true,
  signUpHref = "/auth/signup",
  logInHref = "/auth/login",
  audience,
  compactLogo = false,
}: {
  mode?: GlassNavMode
  onModeChange?: (mode: GlassNavMode) => void
  /** Starting palette when the mode isn't lifted. Match it to the page. */
  defaultMode?: GlassNavMode
  /** Hide the sun/moon on pages that can't repaint with it. */
  showModeToggle?: boolean
  signUpHref?: string
  logInHref?: string
  /** Filters the panel's links. Omit on surfaces with no audience switch. */
  audience?: NavAudience
  /** 25% smaller wordmark — /listings' logged-out nav. */
  compactLogo?: boolean
}) {
  const [internalMode, setInternalMode] = useState<GlassNavMode>(defaultMode)
  const mode = modeProp ?? internalMode
  const setMode = (next: GlassNavMode) => {
    setInternalMode(next)
    onModeChange?.(next)
  }

  const [open, setOpen] = useState(false)
  const shellRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  // Auth. `authReady` gates only the right-hand action, so the pill and its
  // menu paint immediately and just the Join/Dashboard slot settles a beat
  // later — better than holding the whole nav back on a network round-trip.
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [userRole, setUserRole] = useState<"barber" | "owner" | "client" | null>(null)
  const [authReady, setAuthReady] = useState(false)
  // Starts false and flips in an effect, never read during render on the
  // server: isNativeApp() is always false in SSR and true in the shell, so
  // reading it straight would mismatch on hydration and React would throw
  // the markup away. One frame of the web nav inside the app is cheaper,
  // and the panel is shut on that frame anyway.
  const [native, setNative] = useState(false)
  useEffect(() => setNative(isNativeApp()), [])

  useEffect(() => {
    const supabase = createClient()

    const check = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setIsLoggedIn(!!user)
      if (user) {
        const { data: profile } = await supabase.from("profiles").select("role").eq("id", user.id).single()
        if (profile?.role) setUserRole(profile.role as "barber" | "owner" | "client")
      }
      setAuthReady(true)
    }

    check()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: string, session: { user?: unknown } | null) => {
      setIsLoggedIn(!!session?.user)
      // Never call supabase.* synchronously in here — auth-js still holds its
      // navigator.locks mutex and a nested call deadlocks the sign-in that
      // fired the event. Same guard as PublicNavbar.
      if (session?.user) setTimeout(() => check(), 0)
    })

    return () => subscription.unsubscribe()
  }, [])

  const dashboardHref =
    userRole === "owner" ? "/dashboard/owner" : userRole === "client" ? "/dashboard/client" : "/dashboard/barber"

  const signOut = async () => {
    const supabase = createClient()
    await supabase.auth.signOut()
    window.location.href = "/"
  }

  // A page with its own audience switch (the landing) wins. Everywhere else a
  // signed-in visitor's role filters the panel, so a barber isn't offered
  // "Find Barbers" and an owner isn't sent to browse the shops they run.
  const resolvedAudience = audience ?? (isLoggedIn && userRole ? userRole : undefined)

  const primary = forAudience(PRIMARY, resolvedAudience, native)
  const company = forAudience(COMPANY, resolvedAudience, native)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    const onPointer = (e: PointerEvent) => {
      if (!shellRef.current?.contains(e.target as Node)) close()
    }
    window.addEventListener("keydown", onKey)
    window.addEventListener("pointerdown", onPointer)
    return () => {
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("pointerdown", onPointer)
    }
  }, [open, close])

  const isDark = mode === "dark"
  // Nav chrome is always the dark liquid-glass shell — same on the landing,
  // /help, /blog, and light-mode glass pages. Page `mode` only repaints the
  // surface behind it and drives the sun/moon toggle.
  // On light pages (/pricing, /press) the pill goes near-solid black so
  // backdrop blur over white doesn't wash it to gray.
  const navStyle = {
    ...TOKENS.dark,
    ...(isDark
      ? {}
      : {
          "--nav-pill": TOKENS.light["--nav-pill"],
          "--nav-pill-edge": TOKENS.light["--nav-pill-edge"],
        }),
    // The pill sits at the very top of the screen, which in the app is where
    // the status bar and Dynamic Island are — the shell hands the page the
    // whole screen (contentInset: "never").
    //
    // ADDED to the designed gap, not max()'d against it. max() meant the inset
    // REPLACED the 0.75rem on exactly the devices that have an inset, so the
    // nav's contents sat flush against the bottom of the clock with no
    // breathing room at all. calc() keeps the 0.75rem everywhere and clears
    // the status bar where there is one; env() is 0 in a browser, so the web
    // is unchanged.
    //
    // Set here rather than as a class because Tailwind arbitrary values
    // silently fail to build in this project.
    paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)",
  } as React.CSSProperties

  return (
    <div
      ref={shellRef}
      style={navStyle}
      className="fixed inset-x-0 top-0 z-[70] flex justify-center px-3 sm:px-4"
    >
      <div className="w-full max-w-[430px]">
        {/* Glass shell. Closed it has no padding and no background, so only
            the pill shows; open it inflates into the panel.

            The backdrop blur is CONSTANT rather than animated. Transitioning
            backdrop-filter means the compositor re-blurs everything behind the
            element on every frame, and that was the stutter — it doesn't need
            to move anyway: closed, this box is exactly the pill, and the pill
            carries its own blur over a near-opaque fill, so a blur underneath
            it is invisible. It only starts showing as the panel grows. */}
        <div
          className="relative overflow-hidden rounded-[26px] [will-change:padding]"
          style={{
            padding: open ? 7 : 0,
            background: open ? "var(--nav-panel)" : "transparent",
            border: `1px solid ${open ? "var(--nav-panel-edge)" : "transparent"}`,
            boxShadow: open ? "var(--nav-shadow)" : "0 0 0 rgba(0,0,0,0)",
            backdropFilter: "blur(30px) saturate(165%)",
            WebkitBackdropFilter: "blur(30px) saturate(165%)",
            // Closed, the shell is invisible but still blurs, and a hover inside
            // it (theme toggle, Join) could repaint that blur as a square box
            // behind the pill. The rounded clip pins it to the pill's shape. It
            // costs nothing closed (overflow-hidden already clips the bar's
            // shadow) and comes off when open so the panel keeps its own shadow.
            clipPath: open ? undefined : "inset(0 round 26px)",
            transition: `padding 420ms ${EASE}, background 300ms ease, border-color 300ms ease, box-shadow 420ms ease`,
          }}
        >
          {/* Specular gloss + grain — the "liquid glass" read. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              opacity: open ? 1 : 0,
              transition: `opacity 300ms ${EASE}`,
              background:
                "linear-gradient(168deg, var(--nav-gloss) 0%, rgba(255,255,255,0.04) 22%, transparent 46%)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 mix-blend-overlay"
            style={{
              opacity: open ? 0.22 : 0,
              transition: `opacity 300ms ${EASE}`,
              backgroundImage: GRAIN_URL,
            }}
          />

          {/* ── Bar ─────────────────────────────────────────────── */}
          <div
            className="relative flex h-11 items-center justify-between rounded-[20px] pl-3.5 pr-2"
            style={{
              background: "var(--nav-pill)",
              border: "1px solid var(--nav-pill-edge)",
              color: "var(--nav-pill-fg)",
              backdropFilter: "blur(24px) saturate(160%)",
              WebkitBackdropFilter: "blur(24px) saturate(160%)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.14), 0 12px 34px -18px rgba(0,0,0,0.9)",
              transition: `background 520ms ease, border-color 520ms ease, color 420ms ease`,
            }}
          >
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={panelId}
              className="group flex h-full items-center gap-2.5 pr-3 outline-none"
            >
              <MenuGlyph open={open} />
              {/* Menu ⇄ Close crossfade in a fixed box so nothing reflows.
                  The box has to be at least as tall as the 13px text's line
                  box or overflow-hidden crops the descenders and the label
                  reads as sitting low against the glyph — hence h-[18px] plus
                  flex centring on each layer rather than a bare inset-0. */}
              <span className="relative block h-[18px] w-[46px] overflow-hidden text-left">
                <span
                  className="absolute inset-0 flex items-center text-[13px] font-medium leading-none tracking-tight"
                  style={{
                    opacity: open ? 0 : 1,
                    transform: open ? "translateY(-100%)" : "translateY(0)",
                    transition: `opacity 320ms ease, transform 420ms ${EASE}`,
                  }}
                >
                  Menu
                </span>
                <span
                  className="absolute inset-0 flex items-center text-[13px] font-medium leading-none tracking-tight"
                  style={{
                    opacity: open ? 1 : 0,
                    transform: open ? "translateY(0)" : "translateY(100%)",
                    transition: `opacity 320ms ease, transform 420ms ${EASE}`,
                  }}
                >
                  Close
                </span>
              </span>
            </button>

            {/* BOOTH wordmark — dead centre of the pill, links home. The
                white-ink asset is hardcoded rather than theme-swapped: this
                pill is dark in BOTH colour modes (smoked glass over black in
                dark, near-solid black in light), so the black-ink variant
                would vanish into it. Width/height are fixed so the centred
                mark never shifts while the image decodes. */}
            <Link
              href={native ? "/search" : "/"}
              aria-label="BOOTH home"
              className="absolute left-1/2 -translate-x-1/2 opacity-90 transition-opacity hover:opacity-100"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/booth-wordmark-dark.webp"
                alt="BOOTH"
                width={compactLogo ? 65 : 86}
                height={compactLogo ? 11 : 15}
                className={compactLogo ? "h-[11px] w-auto" : "h-[15px] w-auto"}
              />
            </Link>

            <div className="flex items-center gap-1.5">
              {showModeToggle && (
                <button
                  type="button"
                  onClick={() => setMode(isDark ? "light" : "dark")}
                  aria-label={isDark ? "Switch to light" : "Switch to dark"}
                  className="flex h-7 w-7 items-center justify-center rounded-full transition-transform duration-300 hover:rotate-12 active:scale-90"
                  style={{
                    border: "1px solid currentColor",
                    borderColor: "rgba(255,255,255,0.22)",
                  }}
                >
                  {isDark ? <MoonGlyph /> : <SunGlyph />}
                </button>
              )}
              {/* Short round pill in the project's standard CTA shape
                  (h-8, text-xs, rounded-full). One word so it stays compact
                  at 390px — the bar's right cluster lands ~96px, which
                  balances the ~93px "Menu" cluster on the left and keeps the
                  centred wordmark optically centred.

                  Signed in it reads Dashboard: "Join" to someone who already
                  joined is the wrong door, and this pill is the only action in
                  the bar. Held blank until auth resolves so it never flashes
                  Join at a signed-in visitor — a fixed min-width keeps the
                  wordmark from shifting while it settles. */}
              <span className="inline-flex min-w-[56px] justify-end">
                {authReady &&
                  (isLoggedIn ? (
                    <Link
                      href={dashboardHref}
                      className="inline-flex h-8 items-center justify-center rounded-full px-3.5 text-xs font-medium tracking-tight transition-transform duration-200 hover:scale-[1.03] active:scale-95"
                      style={{
                        background: "#FAFAF8",
                        color: "#0A0A0A",
                        boxShadow: "0 2px 12px rgba(0,0,0,0.28)",
                      }}
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <Link
                      href={signUpHref}
                      className="inline-flex h-8 items-center justify-center rounded-full px-3.5 text-xs font-medium tracking-tight transition-transform duration-200 hover:scale-[1.03] active:scale-95"
                      style={{
                        background: "#FAFAF8",
                        color: "#0A0A0A",
                        boxShadow: "0 2px 12px rgba(0,0,0,0.28)",
                      }}
                    >
                      Join
                    </Link>
                  ))}
              </span>
            </div>
          </div>

          {/* ── Collapsible panel ───────────────────────────────── */}
          <div
            id={panelId}
            className="grid [will-change:grid-template-rows]"
            style={{
              gridTemplateRows: open ? "1fr" : "0fr",
              transition: `grid-template-rows 420ms ${EASE}`,
            }}
          >
            <div className="overflow-hidden">
              <div
                className="px-3.5 pb-3 pt-5"
                style={{
                  color: "var(--nav-fg)",
                  opacity: open ? 1 : 0,
                  transition: `opacity 240ms ease ${open ? "60ms" : "0ms"}`,
                }}
              >
                <Label open={open} delay={40}>
                  Menu
                </Label>
                <ul className="mt-2.5 space-y-0.5">
                  {primary.map((link, i) => (
                    <Row key={link.href} open={open} delay={70 + i * 26}>
                      <Link
                        href={link.href}
                        onClick={close}
                        className="group flex items-baseline gap-2 py-[3px] text-[25px] font-light leading-[1.22] tracking-[-0.02em] transition-colors duration-200"
                        style={{ color: "var(--nav-fg)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--nav-accent)")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--nav-fg)")}
                      >
                        {link.label}
                      </Link>
                    </Row>
                  ))}
                </ul>

                <Divider open={open} delay={190} />

                <Label open={open} delay={210}>
                  Company
                </Label>
                <ul className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-[7px]">
                  {company.map((link, i) => (
                    <Row key={link.href} open={open} delay={225 + i * 16}>
                      <NavSmallLink link={link} onNavigate={close} />
                    </Row>
                  ))}
                </ul>

                {/* Legal (Privacy/Terms/Refunds) dropped from the nav panel —
                    still in the footer's Legal column, which is where a
                    visitor actually goes looking for it. */}

                {/* Social links intentionally omitted — the footer already
                    carries them, and in the nav they pushed the auth actions
                    further down the panel for links nobody opens a menu to
                    find. */}

                {/* Auth actions — the one thing the reference menu doesn't
                    have and BOOTH can't ship without. */}
                <Row open={open} delay={400}>
                  <div className="mt-5 flex items-center gap-2">
                    {isLoggedIn ? (
                      <>
                        <Link
                          href={dashboardHref}
                          onClick={close}
                          className="flex h-10 flex-1 items-center justify-center rounded-full text-[13px] font-medium tracking-tight transition-transform duration-200 active:scale-[0.98]"
                          style={{
                            background: "#FAFAF8",
                            color: "#0A0A0A",
                            boxShadow: "0 10px 26px -14px rgba(0,0,0,0.8)",
                          }}
                        >
                          Dashboard
                        </Link>
                        <button
                          type="button"
                          onClick={signOut}
                          className="flex h-10 flex-1 items-center justify-center rounded-full text-[13px] font-medium tracking-tight transition-colors duration-200"
                          style={{
                            background: "var(--nav-chip)",
                            border: "1px solid var(--nav-chip-edge)",
                            color: "var(--nav-fg)",
                          }}
                        >
                          Sign Out
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          href={signUpHref}
                          onClick={close}
                          className="flex h-10 flex-1 items-center justify-center rounded-full text-[13px] font-medium tracking-tight transition-transform duration-200 active:scale-[0.98]"
                          style={{
                            background: "#FAFAF8",
                            color: "#0A0A0A",
                            boxShadow: "0 10px 26px -14px rgba(0,0,0,0.8)",
                          }}
                        >
                          Sign Up
                        </Link>
                        <Link
                          href={logInHref}
                          onClick={close}
                          className="flex h-10 flex-1 items-center justify-center rounded-full text-[13px] font-medium tracking-tight transition-colors duration-200"
                          style={{
                            background: "var(--nav-chip)",
                            border: "1px solid var(--nav-chip-edge)",
                            color: "var(--nav-fg)",
                          }}
                        >
                          Log In
                        </Link>
                      </>
                    )}
                  </div>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Panel primitives ──────────────────────────────────────────── */

// Staggered reveal. Delays only apply on the way in; closing collapses
// everything at once so the panel doesn't "unzip" on the way out.
function Row({ open, delay, children }: { open: boolean; delay: number; children: React.ReactNode }) {
  return (
    <li
      className="list-none"
      style={{
        opacity: open ? 1 : 0,
        transform: open ? "translateY(0)" : "translateY(6px)",
        transition: `opacity 260ms ease ${open ? `${delay}ms` : "0ms"}, transform 360ms ${EASE} ${open ? `${delay}ms` : "0ms"}`,
      }}
    >
      {children}
    </li>
  )
}

function Label({ open, delay, children }: { open: boolean; delay: number; children: React.ReactNode }) {
  return (
    <p
      className="text-[11px] font-medium tracking-tight"
      style={{
        color: "var(--nav-muted)",
        opacity: open ? 1 : 0,
        transform: open ? "translateY(0)" : "translateY(6px)",
        transition: `opacity 240ms ease ${open ? `${delay}ms` : "0ms"}, transform 320ms ${EASE} ${open ? `${delay}ms` : "0ms"}`,
      }}
    >
      {children}
    </p>
  )
}

function Divider({ open, delay }: { open: boolean; delay: number }) {
  return (
    <div
      className="my-4 h-px origin-left"
      style={{
        background: "var(--nav-line)",
        transform: open ? "scaleX(1)" : "scaleX(0)",
        transition: `transform 420ms ${EASE} ${open ? `${delay}ms` : "0ms"}`,
      }}
    />
  )
}

function NavSmallLink({ link, onNavigate }: { link: NavLink; onNavigate: () => void }) {
  const className =
    "inline-block text-[13.5px] font-normal tracking-tight transition-opacity duration-200 hover:opacity-60"
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className} style={{ color: "var(--nav-fg)" }}>
        {link.label}
      </a>
    )
  }
  return (
    <Link href={link.href} onClick={onNavigate} className={className} style={{ color: "var(--nav-fg)" }}>
      {link.label}
    </Link>
  )
}

/* ── Glyphs ────────────────────────────────────────────────────── */

// Two bars that rotate into an X. Drawn with spans rather than swapped SVGs
// so the morph is continuous.
function MenuGlyph({ open }: { open: boolean }) {
  const bar = "absolute left-0 block h-[1.5px] w-full rounded-full bg-current"
  return (
    <span className="relative block h-[11px] w-[15px]">
      <span
        className={bar}
        style={{
          top: open ? 5 : 2,
          transform: open ? "rotate(45deg)" : "rotate(0deg)",
          transition: `top 260ms ${EASE}, transform 420ms ${EASE} ${open ? "120ms" : "0ms"}`,
        }}
      />
      <span
        className={bar}
        style={{
          top: open ? 5 : 8,
          transform: open ? "rotate(-45deg)" : "rotate(0deg)",
          transition: `top 260ms ${EASE}, transform 420ms ${EASE} ${open ? "120ms" : "0ms"}`,
        }}
      />
    </span>
  )
}

function SunGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  )
}

function MoonGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  )
}
